import { TranslateService } from '@ngx-translate/core';
import { Model } from 'src/app/interfaces/api/model';
import { ModelService } from 'src/app/services/api/models/model.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { Config } from '../../../interfaces/shared/config';
import { Cursor } from '../Cursor';
import { UnitShape } from '../shapes/unit';

declare const Autodesk: any;

export class ElementDockingPanel extends Autodesk.Viewing.UI.DockingPanel {
    // Properties
    private modelTableId: string = "1234";
    private datatable: { height: string } = {
        height: "Height"
    };

    // Core dependencies
    private viewer: any;
    private modelService: ModelService;
    private trackerService: TrackerService;
    private translate: TranslateService;
    private model: Model;
    public units: UnitShape[];

    // UI elements
    private modelTable: HTMLTableElement | null = null;
    private modelTableBody: HTMLTableSectionElement | null = null;
    private mainContainer: HTMLDivElement | null = null;
    private cursor: Cursor | null = null;

    // Configuration
    private colors = Config.colors;
    private projectXY: any = null;
    private projectLatLonXY: any = null;
    private updateModels: any = null;
    private offset: any = null;
    private origin: any = null;
    private onChange: ((origin: any) => void) | null = null;

    // Subscriptions
    private cursorSubscription: any = null;
    private modelSubscription: any = null;

    constructor(
        viewer: any,
        container: HTMLElement,
        id: string,
        title: string,
        options: any
    ) {
        // Vérifier que Autodesk est disponible avant d'appeler super()
        if (typeof Autodesk === 'undefined' || !Autodesk.Viewing || !Autodesk.Viewing.UI || !Autodesk.Viewing.UI.DockingPanel) {
            throw new Error('ElementDockingPanel: Autodesk.Viewing.UI.DockingPanel not available');
        }

        super(container, id, title, options);

        this.viewer = viewer;
        this.modelService = options.modelService;
        this.trackerService = options.trackerService;
        this.translate = options.translate;
        this.model = options.model;
        this.units = options.units || [];

        // Style du docking panel
        (this['container'] as HTMLElement).style.top = "20px";
        (this['container'] as HTMLElement).style.left = "20px";
        (this['container'] as HTMLElement).style.width = "320px";
        (this['container'] as HTMLElement).style.height = "auto";
        (this['container'] as HTMLElement).style.minHeight = "120px";
        (this['container'] as HTMLElement).style.resize = "auto";
        (this['container'] as HTMLElement).style.display = 'block';

        const div = document.createElement('div');
        div.classList.add('docking-panel-container-solid-color-a', 'docking-panel-scroll', 'right');
        (this['container'] as HTMLElement).appendChild(div);
        this.mainContainer = div;

        this.loadAndRenderHtml(this.mainContainer).then(() => {
            this.init();
        });
    }

    private enableCursor(): void {
        // Vérifications de sécurité avant d'activer le curseur
        if (!this.viewer || !this.units || this.units.length === 0) {
            console.warn('ElementDockingPanel: Cannot enable cursor - missing viewer or units');
            return;
        }

        // Vérifier que Autodesk est disponible
        if (typeof Autodesk === 'undefined' || !Autodesk.Viewing) {
            console.warn('ElementDockingPanel: Autodesk.Viewing not available');
            return;
        }

        try {
            const cursorOptions = {
                objects: this.units,
                viewer: this.viewer
            };

            this.cursor = new Cursor(cursorOptions);

            if (this.viewer.toolController) {
                this.viewer.toolController.registerTool(this.cursor);
                this.viewer.toolController.activateTool(this.cursor.toolName);

                this.cursorSubscription = this.cursor.selection.subscribe((selections: any[]) => {
                    this.getUnit(selections);
                });
            }
        } catch (error) {
            console.error('ElementDockingPanel: Error enabling cursor:', error);
        }
    }

    private getUnit(selections: any[]): void {
        if (!selections || !Array.isArray(selections) || selections.length === 0) {
            return;
        }

        const units: UnitShape[] = [];

        selections.forEach((s: any) => {
            if (!s || !s.name) return;

            const unit = this.units.find((u: any) => {
                if (u && u.trackers && Array.isArray(u.trackers) && u.trackers[0] && u.trackers[0].name === s.name) {
                    return true;
                }

                return false;
            });

            if (!unit) return;

            const unitIndex = units.findIndex((u: UnitShape) => {
                return u && u.trackers && u.trackers[0] && unit.trackers && unit.trackers[0] &&
                    u.trackers[0].name === unit.trackers[0].name;
            });

            if (unit && unitIndex < 0) {
                units.push(unit);
            }
        });

        this.buildContent(units[0] || null);
    }

    private buildContent(unit: UnitShape | null): void {
        if (!this.mainContainer) return;

        // Récupère le conteneur dynamique
        const contentDiv = this.mainContainer.querySelector('#element-info-content');
        if (!contentDiv) return;

        if (!unit || !unit.trackers || !unit.trackers[0]) {
            contentDiv.innerHTML = `<div class="no-selection">${this.translate.instant('viewer.no_selection')}</div>`;
            return;
        }

        let html = `<h3 class="object-properties-title">${this.translate.instant('planning.object_properties')}</h3>`;

        html += `<div class="object-properties">
                <div class="left">${this.translate.instant('units.unit')}</div>
                <div class="right">${unit.name}</div>
            </div>`;
        html += `<div class="object-properties">
                <div class="left">${this.translate.instant('units.tracker')}</div>
                <div class="right">${unit.trackers[0].name}</div>
            </div>`;

        const mapping: { [key: string]: any } = {};
        const lastTrack = unit.trackers[0].lastTrack;

        if (lastTrack) {
            const track = lastTrack;
            if (track.lat !== undefined) mapping['Lat'] = track.lat;
            if (track.lon !== undefined) mapping['Lon'] = track.lon;
            if (track.x !== undefined) mapping['X'] = track.x;
            if (track.y !== undefined) mapping['Y'] = track.y;
            if (track.z !== undefined) mapping['Z'] = track.z;
            if (track.createdDate !== undefined) mapping['Created Date'] = track.createdDate;

            if (track.message) {
                Object.keys(track.message).forEach(k => {
                    mapping[k] = track.message![k];
                });
            }
        }

        Object.keys(mapping).forEach(k => {
            html += `<div class="object-properties">
                    <div class="left">${k}</div>
                    <div class="right">${mapping[k]}</div>
                </div>`;
        });

        contentDiv.innerHTML = html;
    }

    private buildTree(objectTree: any): void {
        if (!this.mainContainer) return;

        let html = `<h3 class="object-properties-title">${this.translate.instant('planning.object_properties')}</h3>`;

        Object.keys(objectTree).forEach(k => {
            html += `<div class="object-properties-subTitle">
            <h4>${k}</h4>
            </div>`;

            objectTree[k].forEach((p: any) => {
                let value = '';
                if (p.type === 1) {
                    value = p.displayValue ? 'Yes' : 'No';
                } else if (p.type === 2) {
                    value = p.displayValue;
                } else if (p.type === 3) {
                    value = p.displayValue + ' ' + p.units;
                }

                if (!value) value = "None";

                html += ` 
                    <div id="global-object-properties">
                    <div id="left">${p.displayName}</div>
                    <div id="right">${value}</div>
                    </div>`;
            });

            html += '<br/>';
        });

        this.mainContainer.innerHTML += html;
        this.mainContainer.style.width = "290px";
        this.mainContainer.style.height = "250px";
        this.mainContainer.style.overflowY = "auto";
        this.mainContainer.style.overflow = "auto";
    }

    public init(reload: boolean = false): void {
        if (!this.units || this.units.length === 0) {
            console.warn('ElementDockingPanel: No units available, not activating cursor');
            return;
        }

        this.enableCursor();
    }

    private edit(e: Event): void {
        const input = e.target as HTMLInputElement;
        let value: string | number = input.value;
        const id = input.getAttribute('id');
        const type = input.getAttribute('type');

        if (type === 'number') value = parseFloat(value);
        if (!id) return;

        const attrName = id.split(".")[0];
        const parts = id.split(".");
        if (attrName === "position" && parts.length > 1 && parts[1]) {
            this.origin.position[parts[1]] = value;
        } else {
            this.origin[id] = value;
        }

        this.model.origin = this.origin;

        if (this.onChange) {
            this.onChange(this.origin);
        }
    }

    private onOriginChanged(): void {
        // Implementation if needed
    }

    private updateModel(): void {
        this.modelSubscription = this.modelService.updateModel(this.model).subscribe({
            next: (res: any) => {
                // Handle response if needed
            },
            error: (err: any) => {
                console.error('Error updating model:', err);
            }
        });
    }

    public destroy(): void {
        try {
            if (this.updateModels) {
                this.updateModels.unsubscribe();
                this.updateModels = null;
            }

            if (this.modelSubscription) {
                this.modelSubscription.unsubscribe();
                this.modelSubscription = null;
            }

            if (this.cursorSubscription) {
                this.cursorSubscription.unsubscribe();
                this.cursorSubscription = null;
            }

            if (this.cursor && this.viewer && this.viewer.toolController) {
                try {
                    this.viewer.toolController.deregisterTool(this.cursor);
                } catch (error) {
                    console.warn('ElementDockingPanel: Error deregistering cursor tool:', error);
                }
            }

            this.mainContainer = null;
            this.cursor = null;
        } catch (error) {
            console.error('ElementDockingPanel: Error in destroy():', error);
        }
    }

    async loadAndRenderHtml(div: HTMLDivElement) {
        const template = await this.loadHtmlTemplate('viewer/elementInfo/elementInfo-extension/elementInfo-extension.html');
        const variables = {
            viewer_element_info: this.translate.instant('viewer.element_info'),
            viewer_no_selection: this.translate.instant('viewer.no_selection')
        };

        div.innerHTML = this.renderTemplate(template, variables);
    }

    async loadHtmlTemplate(path: string): Promise<string> {
        const response = await fetch(path);
        return await response.text();
    }

    renderTemplate(template: string, variables: Record<string, string | number>) {
        return template.replace(/{{(.*?)}}/g, (_, key) => String(variables[key.trim()] ?? ''));
    }
}